import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid2,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import { CloudUpload, ExpandMore, ExpandLess, CheckCircle, Error } from '@mui/icons-material';
import { useUploadMigrationFileMutation, MigrationResult } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';

export const MigrationDebug: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const { toggleSnackbar } = useSnackbar();

  const [uploadMigrationFile, { isLoading, error }] = useUploadMigrationFileMutation();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith('.sql')) {
      setSelectedFile(file);
      setMigrationResult(null);
    } else {
      alert('Please select a .sql file');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      const result = await uploadMigrationFile(selectedFile).unwrap();
      setMigrationResult(result);
    } catch (err) {
      console.error('Migration failed:', err);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const renderMigrationSection = (title: string, data: any, sectionKey: string) => {
    if (!data) return null;

    const hasErrors = data.errors && data.errors.length > 0;
    const hasSkipped =
      data.skippedUsers ||
      data.skippedPromos ||
      data.skippedEmployers ||
      data.skippedJobseekers ||
      data.skippedJobs ||
      data.skippedApplications ||
      data.skippedCampaigns ||
      data.skippedInvoices ||
      data.skippedMessages;

    return (
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasErrors ? <Error color="error" /> : <CheckCircle color="success" />}
              {title}
              {hasErrors && <Chip label={`${data.errors.length} errors`} color="error" size="small" />}
              {hasSkipped && <Chip label={`${hasSkipped} skipped`} color="warning" size="small" />}
            </Typography>
            <IconButton onClick={() => toggleSection(sectionKey)}>
              {expandedSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(data).map(([key, value]) => {
              if (key === 'errors') return null;
              return (
                <Grid2 key={key} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {formatNumber(value as number)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </Typography>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>

          <Collapse in={expandedSections[sectionKey]}>
            {hasErrors && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors ({data.errors.length}):
                </Typography>
                <List dense>
                  {data.errors.slice(0, 10).map((error: string, index: number) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={error}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            color: 'error.main',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                  {data.errors.length > 10 && (
                    <ListItem>
                      <ListItemText
                        primary={`... and ${data.errors.length - 10} more errors`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </Collapse>
        </Box>
      </Paper>
    );
  };

  useEffect(() => {
    if (error) {
      console.error(error);
      toggleSnackbar('error', 'Migration failed');
    }
  }, [error]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Migration Debug Tool
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload a SQL dump file to migrate legacy data to the new system.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <input
              accept=".sql"
              style={{ display: 'none' }}
              id="sql-file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="sql-file-upload">
              <Button variant="outlined" component="span" startIcon={<CloudUpload />} sx={{ mr: 2 }}>
                Select SQL File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedFile || isLoading}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? 'Processing...' : 'Start Migration'}
          </Button>

          {isLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processing migration... This may take several minutes.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {migrationResult && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Migration Results
          </Typography>

          {renderMigrationSection('Users', migrationResult.users, 'users')}
          {renderMigrationSection('Promo Codes', migrationResult.promos, 'promos')}
          {renderMigrationSection('Employers & Rooftops', migrationResult.employers, 'employers')}
          {renderMigrationSection('Jobseekers & Profiles', migrationResult.jobseekers, 'jobseekers')}
          {renderMigrationSection('Jobs & Posts', migrationResult.posts, 'posts')}
          {renderMigrationSection('Applications', migrationResult.applications, 'applications')}
          {renderMigrationSection('Campaigns', migrationResult.campaigns, 'campaigns')}
          {renderMigrationSection('Invoices', migrationResult.invoices, 'invoices')}
          {renderMigrationSection('Chat Messages', migrationResult.chat, 'chat')}
        </Box>
      )}
    </Box>
  );
};
